import React, { useState } from "react"
import dayjs, { Dayjs } from "dayjs"
import { FeedSpinner } from "../../components/shared/Spinner"
import { RequestError } from "../../components/shared/RequestError"
import { UserInteractionForm } from "../../components/UserInteractionForm"
import { useBandPlayStats } from "../../hooks/useBandPlayStats"
import { useBandAlbumsWithSongs } from "../../hooks/useBandAlbumsWithSongs"
import { PlayStatsChart } from "../../components/UserInteractionStatCard/PlayStatsChart"

interface UserInteractionsProps {
  bandId: number
}

interface StatGroup {
  play: number
  partial: number
  skip: number
}

export const UserInteractions: React.FC<UserInteractionsProps> = ({
  bandId,
}) => {
  const [start, setStart] = useState<Dayjs>(dayjs().subtract(1, "year"))
  const [end, setEnd] = useState<Dayjs>(dayjs().subtract(1, "day"))
  const [selectedPostId, setSelectedPostId] = useState<string>("")
  const [selectedSongTitle, setSelectedSongTitle] = useState<string>("")

  const { status, data, error } = useBandPlayStats(
    bandId,
    start.format("YYYY-MM-DD"),
    end.format("YYYY-MM-DD"),
    selectedPostId || null,
  )
  const albumsQuery = useBandAlbumsWithSongs(bandId)

  const songsByAlbum: Record<string, Array<{ id: number; title: string }>> = {}
  if (albumsQuery.status === "success") {
    const albums: any[] = albumsQuery.data?.posts || []
    albums.forEach((album) => {
      const songs: any[] = album.songs || []
      if (songs.length === 0) return
      songsByAlbum[album.title] = songs.map((s) => ({
        id: s.id,
        title: s.title,
      }))
    })
  }

  const web: StatGroup = data?.found_interactions?.web ?? {
    play: 0,
    partial: 0,
    skip: 0,
  }
  const app: StatGroup = data?.found_interactions?.app ?? {
    play: 0,
    partial: 0,
    skip: 0,
  }
  const embed: StatGroup = data?.found_interactions?.embed ?? {
    play: 0,
    partial: 0,
    skip: 0,
  }

  const rows = [
    { label: "Full Plays", key: "play" as const },
    { label: "Partials", key: "partial" as const },
    { label: "Skips", key: "skip" as const },
  ]

  return (
    <div>
      <h2 className="border-b-4 mt-4 border-black font-bold text-xl uppercase mb-3">
        Music Play Stats
      </h2>

      <div className="grid grid-cols-12 gap-6">
        {/* Left — filters */}
        <div className="lg:col-span-8 col-span-12 flex flex-col gap-3 mt-5">
          <UserInteractionForm
            startDate={start}
            endDate={end}
            onStartChange={(val: Dayjs) => val && setStart(val)}
            onEndChange={(val: Dayjs) => val && setEnd(val)}
          />
          <div>
            <select
              value={selectedPostId}
              onChange={(e) => {
                const opt = e.target.options[e.target.selectedIndex]
                setSelectedPostId(e.target.value)
                setSelectedSongTitle(e.target.value ? opt.text : "")
              }}
              className="border border-gray-400 rounded px-2 py-1 text-sm w-full"
            >
              <option value="">All songs</option>
              {Object.entries(songsByAlbum).map(([album, songs]) => (
                <optgroup key={album} label={album}>
                  {songs.map((song) => (
                    <option key={song.id} value={song.id}>
                      {song.title}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
        </div>

        {/* Right — stats table */}
        {status === "loading" ? (
          <FeedSpinner />
        ) : status === "error" ? (
          <RequestError error={error} />
        ) : status === "success" ? (
          <table className="self-start border-collapse text-sm lg:col-span-4 col-span-12">
            <thead>
              <tr>
                <th className="text-left pr-4 pb-1 font-semibold uppercase text-xs"></th>
                <th className="text-right px-4 pb-1 font-semibold uppercase text-xs">
                  Web
                </th>
                <th className="text-right px-4 pb-1 font-semibold uppercase text-xs">
                  App
                </th>
                <th className="text-right px-4 pb-1 font-semibold uppercase text-xs">
                  Embed
                </th>
                <th className="text-right pl-4 pb-1 font-semibold uppercase text-xs">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ label, key }) => (
                <tr key={key} className="border-t border-gray-200">
                  <td className="pr-4 py-1 font-medium">{label}</td>
                  <td className="text-right px-4 py-1">{web[key]}</td>
                  <td className="text-right px-4 py-1">{app[key]}</td>
                  <td className="text-right px-4 py-1">{embed[key]}</td>
                  <td className="text-right pl-4 py-1 font-semibold">
                    {web[key] + app[key] + embed[key]}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </div>

      {status === "success" && (
        <PlayStatsChart
          data={data?.chart_data}
          granularity={data?.chart_granularity ?? "day"}
        />
      )}
    </div>
  )
}
